/**
 * The feed's seven source lists, read off the document.
 *
 * ADR 0012 shapes a read as `select…(view: DocumentView, …)`, and `useFeedSources()` is one
 * `useMemo` over this one. It was `useFeedData` until #66 — see `use-feed-entries.ts` for the
 * other half of that name.
 *
 * **One of #290's six `withPeople` copies dies here.** The projection resolves through
 * `resolveLinkedPeople`, which applies ADR 0012 rule 5's archived-endpoint check, so collapsing it
 * onto `document/view.ts` preserves behaviour rather than changing it.
 */

import { useMemo } from 'react'
import {
  resolveLinkedPeople,
  useDocumentView,
  useDocumentStatus,
} from '@/document'
import type { DocumentView, Entry, EntryWithPeople } from '@/document'

export type { EntryWithPeople }

export interface FeedData {
  journals: EntryWithPeople[]
  tasks: EntryWithPeople[]
  movies: EntryWithPeople[]
  books: EntryWithPeople[]
  artists: EntryWithPeople[]
  labels: EntryWithPeople[]
  releases: EntryWithPeople[]
}

export function selectFeedSources(doc: DocumentView | null): FeedData | null {
  if (!doc) return null

  // Get all entries excluding archived ones
  const allEntries = Object.values(doc.entries).filter(
    (entry) => !entry.archivedAt,
  )

  // Helper to attach people and topics to entries
  const withPeople = (entry: Entry): EntryWithPeople => {
    const linkedPeople = resolveLinkedPeople(entry.id, doc)
    // Map people entries to {id, name} format expected by components
    const people = linkedPeople.map((p) => ({
      id: p.id,
      name: p.title, // Person name is stored in title field
    }))

    // Resolve topics from topicIds
    const topics = (entry.topicIds || [])
      .map((topicId) => doc.topics[topicId])
      .filter((topic) => topic) // Filter out undefined topics
      .map((topic) => ({
        id: topic.id,
        name: topic.name,
      }))

    return {
      ...entry,
      people,
      topics,
    }
  }

  // Separate by type with people
  const journals = allEntries
    .filter((e) => e.type === 'journal')
    .map(withPeople)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const tasks = allEntries
    .filter((e) => e.type === 'task')
    .map(withPeople)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const movies = allEntries
    .filter((e) => e.type === 'movie')
    .map(withPeople)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const books = allEntries
    .filter((e) => e.type === 'book')
    .map(withPeople)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const artists = allEntries
    .filter((e) => e.type === 'artist')
    .map(withPeople)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const labels = allEntries
    .filter((e) => e.type === 'label')
    .map(withPeople)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const releases = allEntries
    .filter((e) => e.type === 'release' || e.type === 'album')
    .map(withPeople)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  return {
    journals,
    tasks,
    movies,
    books,
    artists,
    labels,
    releases,
  } as FeedData
}

/**
 * `selectFeedSources` bound to this device's document.
 *
 * `enabled` is accepted and ignored: the document is already local, so there is nothing to defer.
 * `unified-feed-lazy.tsx` passes a viewport check through it, which predates the Automerge move.
 */
export function useFeedSources(enabled = true) {
  void enabled
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const data = useMemo(() => selectFeedSources(doc), [doc])

  return {
    data,
    isLoading: !ready,
    error: null,
    isSuccess: ready,
    isError: false,
  }
}
